import type {
  CoverageAttach,
  CoverageProfileEntry,
  Customer,
  ProposalDetails,
  RiskEntityPayload,
} from '@/services/quotes/types';
import type { CoverageUmbrella } from '@/services/coverages/types';
import { formatCurrency } from '@/shared/utils/currencyFormatter';
import { removeFormatRegisterNumber } from '@/shared/utils/remove-format-register-number';
import {
  isCapitalGlobalLegalPersonIdType,
  resolveCapitalGlobalIdType,
} from '@/features/quotes/utils/resolve-capital-global-id-type.util';
import type { CapitalGlobalCoverageCatalogItem } from '@/features/quotes/schemas/capital-global/coverages-catalog';
import {
  isGroupedAssistanceBenefit,
  isStandaloneAssistanceBenefit,
} from '@/features/quotes/schemas/capital-global/coverages-catalog';
import {
  capCapitalAndPercent,
  getEffectiveLimits,
  getInitialCoverageValues,
  getPercentReferenceCapital,
  roundCoveragePercent,
} from '@/features/quotes/schemas/capital-global/coverage-limits.util';
import { propagateMorteCapitalChange } from './propagate-morte-capital.util';
import type { StepCoveragesFormValues } from '@/features/quotes/schemas/capital-global/step-coverages.schema';
import type {
  CoveragesDataMap,
  CoveragesEntityValues,
  EntityRiskValues,
  EntityTab,
  RiskDataMap,
  SubStipulantEntry,
} from '../../types';

// ─── resolve-ids-after-create ───────────────────────────────────────────────

export function resolveIdsAfterCreate(data: {
  id?: string;
  quoteId?: string;
}): {
  policyId?: string;
  quoteId?: string;
} {
  const d = data as Record<string, unknown>;
  const nested = d.data as Record<string, unknown> | undefined;

  const policyId =
    (typeof d.id === 'string' && d.id.trim()) ||
    (typeof d.policyId === 'string' && d.policyId.trim()) ||
    (nested && typeof nested.id === 'string' && (nested.id as string).trim()) ||
    undefined;

  const quoteId =
    (typeof d.quoteId === 'string' && d.quoteId.trim()) ||
    (typeof d.quotationNo === 'string' && d.quotationNo.trim()) ||
    (typeof d.proposalNo === 'string' && d.proposalNo.trim()) ||
    (nested &&
      typeof nested.quoteId === 'string' &&
      (nested.quoteId as string).trim()) ||
    undefined;

  return { policyId, quoteId };
}

// ─── map-sub-stipulant-to-customer ──────────────────────────────────────────

function averageAgeFieldsFromEntry(
  entry: Pick<
    SubStipulantEntry,
    | 'averagePartnerAge'
    | 'averageEmployeeAge'
    | 'partnerCount'
    | 'employeeCount'
  >
): Pick<
  Customer,
  'averagePartnerAge' | 'averageEmployeeAge' | 'partnerCount' | 'employeeCount'
> {
  return {
    ...(Number.isFinite(entry.averagePartnerAge)
      ? { averagePartnerAge: entry.averagePartnerAge }
      : {}),
    ...(Number.isFinite(entry.averageEmployeeAge)
      ? { averageEmployeeAge: entry.averageEmployeeAge }
      : {}),
    ...(Number.isFinite(entry.partnerCount)
      ? { partnerCount: entry.partnerCount }
      : {}),
    ...(Number.isFinite(entry.employeeCount)
      ? { employeeCount: entry.employeeCount }
      : {}),
  };
}

function baseFromEntry(
  entry: SubStipulantEntry
): Pick<
  Customer,
  | 'registerNumber'
  | 'cpf'
  | 'idType'
  | 'name'
  | 'socialName'
  | 'segment'
  | 'cnae'
  | 'averagePartnerAge'
  | 'averageEmployeeAge'
  | 'partnerCount'
  | 'employeeCount'
> {
  const idType = resolveCapitalGlobalIdType({
    documentNumber: entry.documentNumber,
    useIndividualDocument: entry.useIndividualDocument,
  });
  return {
    registerNumber: removeFormatRegisterNumber(entry.documentNumber),
    ...(entry.cpf ? { cpf: removeFormatRegisterNumber(entry.cpf) } : {}),
    idType,
    name: entry.name.trim(),
    socialName: '',
    segment: entry.segment || '',
    cnae: entry.cnae || '',
    ...averageAgeFieldsFromEntry(entry),
  };
}

function resolveSubStipulantSequenceNumber(
  entry: SubStipulantEntry,
  sequenceIndex: number
): number {
  const fromEntry = Number(entry.sequenceNumber);
  if (Number.isInteger(fromEntry) && fromEntry >= 1) {
    return fromEntry;
  }
  return sequenceIndex + 1;
}

export function mapSubStipulantToCreateCustomer(
  entry: SubStipulantEntry,
  sequenceIndex: number
): Customer {
  const idType = resolveCapitalGlobalIdType({
    documentNumber: entry.documentNumber,
    useIndividualDocument: entry.useIndividualDocument,
  });
  return {
    ...baseFromEntry(entry),
    sequenceNumber: resolveSubStipulantSequenceNumber(entry, sequenceIndex),
    ...(isCapitalGlobalLegalPersonIdType(idType)
      ? {}
      : {
          gender: '',
          dateOfBirth: '',
        }),
  };
}

/** Corpo de `customer` no attach update (espelho do estipulante; `isPolicyHolder: N`). */
export function mapSubStipulantToUpdateCustomer(
  entry: SubStipulantEntry
): Customer {
  const idType = resolveCapitalGlobalIdType({
    documentNumber: entry.documentNumber,
    useIndividualDocument: entry.useIndividualDocument,
  });
  const sequenceNumber = Number(entry.sequenceNumber);
  if (!Number.isInteger(sequenceNumber) || sequenceNumber < 1) {
    throw new Error(
      'Subestipulante sem `sequenceNumber` válido. Recarregue a cotação e tente novamente.'
    );
  }
  return {
    ...baseFromEntry(entry),
    sequenceNumber,
    documentNumber: removeFormatRegisterNumber(entry.documentNumber),
    documentType: idType,
    cellphone: '',
    phone: '',
    email: '',
    isInsured: 'Y',
    isPolicyHolder: 'N',
    isOrgParty: 'N',
    genderCode: '',
    ...(isCapitalGlobalLegalPersonIdType(idType)
      ? {}
      : {
          dateOfBirth: '',
        }),
  };
}

// ─── map-proposal-details-to-risk-data-map ──────────────────────────────────

function toFormNumber(value: number): string {
  return value > 0 ? String(value) : '';
}

function toFormCurrency(value: number): string {
  return value > 0 ? formatCurrency(value) : '';
}

function profileToEntityRiskValues(
  profile: RiskEntityPayload
): EntityRiskValues {
  if (profile.groupProfile === 'ESCALONADO') {
    return {
      perfilDoGrupo: 'ESCALONADO',
      sdQuantidadeDeVidas: toFormNumber(
        profile.partnersAndDirectors.livesQuantity
      ),
      sdCapitalIndividual: toFormCurrency(
        profile.partnersAndDirectors.individualCapital
      ),
      colQuantidadeDeVidas: toFormNumber(profile.employees.livesQuantity),
      colCapitalIndividual: toFormCurrency(profile.employees.individualCapital),
      tipoDePerfil: 'COLABORADORES',
      uniQuantidadeDeVidas: '',
      uniCapitalIndividual: '',
    };
  }

  // UNIFORME: determinar tipoDePerfil pelo que está zerado
  const sdZero =
    profile.partnersAndDirectors.livesQuantity === 0 &&
    profile.partnersAndDirectors.individualCapital === 0;
  const colZero =
    profile.employees.livesQuantity === 0 &&
    profile.employees.individualCapital === 0;
  const tipoDePerfil = sdZero
    ? 'COLABORADORES'
    : colZero
      ? 'SOCIOS'
      : 'COLABORADORES_E_SOCIOS';

  const band = sdZero ? profile.employees : profile.partnersAndDirectors;

  return {
    perfilDoGrupo: 'UNIFORME',
    sdQuantidadeDeVidas: '',
    sdCapitalIndividual: '',
    colQuantidadeDeVidas: '',
    colCapitalIndividual: '',
    tipoDePerfil,
    uniQuantidadeDeVidas: toFormNumber(band.livesQuantity),
    uniCapitalIndividual: toFormCurrency(band.individualCapital),
  };
}

/**
 * Converte o `riskProfileList` retornado pelo GET em um `RiskDataMap`
 * (chaveado por `documentNumber`) para pré-popular o formulário do step 2.
 */
export function mapProposalDetailsToRiskDataMap(
  proposalDetails: ProposalDetails | undefined
): RiskDataMap {
  const profiles = proposalDetails?.riskProfileList;
  if (!profiles || profiles.length === 0) return {};

  return profiles.reduce<RiskDataMap>((acc, profile) => {
    acc[profile.documentNumber] = profileToEntityRiskValues(profile);
    return acc;
  }, {});
}

// ─── map-proposal-details-to-risk-entities ──────────────────────────────────

export function mapProposalDetailsToRiskEntities(
  proposalDetails: ProposalDetails | undefined
): EntityTab[] {
  if (!proposalDetails) return [];

  const subCustomers = (proposalDetails.customers ?? [])
    .filter(c => c.sequenceNumber !== undefined && c.sequenceNumber !== 0)
    .sort((a, b) => (a.sequenceNumber ?? 0) - (b.sequenceNumber ?? 0));

  const stipulant: EntityTab = {
    id: proposalDetails.customer?.documentNumber ?? 'stipulant',
    label: proposalDetails.customer?.name?.trim() || 'Estipulante',
    isStipulant: true,
    sequenceNumber: 0,
  };

  const subs: EntityTab[] = subCustomers.map((c, index) => ({
    id: c.documentNumber ?? String(c.sequenceNumber ?? index + 1),
    label: c.name?.trim() || `Subestipulante ${index + 1}`,
    isStipulant: false,
    sequenceNumber: c.sequenceNumber ?? index + 1,
  }));

  return [stipulant, ...subs];
}

export function riskEntitiesFingerprint(
  proposalDetails: ProposalDetails | undefined
): string {
  return mapProposalDetailsToRiskEntities(proposalDetails)
    .map(entity => entity.id)
    .join('|');
}

/** Tracks step-2 individual capitals per entity; used to detect changes after revisiting step 3. */
export function riskProfileCapitalsFingerprint(
  proposalDetails: ProposalDetails | undefined
): string {
  const profiles = proposalDetails?.riskProfileList;
  if (!profiles?.length) return '';

  return profiles
    .map(profile => {
      const doc = removeFormatRegisterNumber(profile.documentNumber);
      return `${doc}:${getReferenceCapital(profile)}`;
    })
    .sort()
    .join('|');
}

function pruneRiskDataMapToEntities(
  map: RiskDataMap,
  entities: EntityTab[]
): RiskDataMap {
  const allowedIds = new Set(entities.map(entity => entity.id));
  return Object.fromEntries(
    Object.entries(map).filter(([entityId]) => allowedIds.has(entityId))
  );
}

export function mapProposalDetailsToRiskDataMapForEntities(
  proposalDetails: ProposalDetails | undefined,
  entities: EntityTab[]
): RiskDataMap {
  return pruneRiskDataMapToEntities(
    mapProposalDetailsToRiskDataMap(proposalDetails),
    entities
  );
}

// ─── get-reference-capital ───────────────────────────────────────────────────

export type GroupReferenceCapitals = {
  partnersAndDirectors: number;
  employees: number;
  largest: number;
  largerMemberCategoryCode: '1' | '2';
  smallerMemberCategoryCode: '1' | '2';
  smallerGroupCapital: number;
  smallerGroupLabel: string;
  largerGroupLabel: string;
};

const MEMBER_CATEGORY_LABELS: Record<'1' | '2', string> = {
  '1': 'Colaboradores',
  '2': 'Sócios/Diretores',
};

/**
 * Capitais individuais por grupo no perfil escalonado.
 */
export function getGroupReferenceCapitals(
  riskProfile: RiskEntityPayload | undefined
): GroupReferenceCapitals | null {
  if (!riskProfile || riskProfile.groupProfile !== 'ESCALONADO') {
    return null;
  }

  const partnersAndDirectors =
    riskProfile.partnersAndDirectors?.individualCapital ?? 0;
  const employees = riskProfile.employees?.individualCapital ?? 0;
  const largest = Math.max(partnersAndDirectors, employees);
  const largerMemberCategoryCode: '1' | '2' =
    partnersAndDirectors >= employees ? '2' : '1';
  const smallerMemberCategoryCode: '1' | '2' =
    largerMemberCategoryCode === '1' ? '2' : '1';
  const smallerGroupCapital =
    smallerMemberCategoryCode === '1' ? partnersAndDirectors : employees;

  return {
    partnersAndDirectors,
    employees,
    largest,
    largerMemberCategoryCode,
    smallerMemberCategoryCode,
    smallerGroupCapital,
    largerGroupLabel: MEMBER_CATEGORY_LABELS[largerMemberCategoryCode],
    smallerGroupLabel: MEMBER_CATEGORY_LABELS[smallerMemberCategoryCode],
  };
}

/**
 * Derives the reference individual capital from a risk profile entry.
 *
 * ESCALONADO: maior capital entre sócios e colaboradores (input único Step 3).
 * UNIFORME: the non-zero band's individual capital.
 */
export function getReferenceCapital(
  riskProfile: RiskEntityPayload | undefined
): number {
  if (!riskProfile) return 0;
  if (riskProfile.groupProfile === 'ESCALONADO') {
    const groupCapitals = getGroupReferenceCapitals(riskProfile);
    return groupCapitals?.largest ?? 0;
  }
  const sd = riskProfile.partnersAndDirectors?.individualCapital ?? 0;
  const col = riskProfile.employees?.individualCapital ?? 0;
  return sd > 0 ? sd : col;
}

/**
 * `true` quando o perfil é Uniforme com apenas colaboradores — banda de
 * sócios/diretores zerada e banda de colaboradores preenchida. Único caso em
 * que coberturas exclusivas de sócio (`partnerOnly`) devem ser ocultadas no
 * step de coberturas. Espelha a derivação de `tipoDePerfil` em
 * `profileToEntityRiskValues`.
 */
export function isUniformeColaboradoresOnly(
  riskProfile: RiskEntityPayload | undefined
): boolean {
  if (!riskProfile || riskProfile.groupProfile !== 'UNIFORME') {
    return false;
  }
  const sdZero =
    (riskProfile.partnersAndDirectors?.livesQuantity ?? 0) === 0 &&
    (riskProfile.partnersAndDirectors?.individualCapital ?? 0) === 0;
  const colZero =
    (riskProfile.employees?.livesQuantity ?? 0) === 0 &&
    (riskProfile.employees?.individualCapital ?? 0) === 0;
  return sdZero && !colZero;
}

function resolveCoveragesForPrefill(
  entry: CoverageProfileEntry,
  riskProfile: RiskEntityPayload | undefined
): CoverageAttach[] {
  const groupCapitals = getGroupReferenceCapitals(riskProfile);

  if (groupCapitals && entry.groups?.length) {
    const largestGroup = entry.groups.find(
      g => g.memberCategoryCode === groupCapitals.largerMemberCategoryCode
    );
    if (largestGroup?.coverages?.length) {
      return largestGroup.coverages;
    }
  }

  return entry.coverages ?? [];
}

// ─── map-coverage-profile-to-form-values ────────────────────────────────────

export function mapCoverageProfileToFormValues(
  entry: CoverageProfileEntry,
  coverageCatalog: CapitalGlobalCoverageCatalogItem[],
  morteId: string | undefined,
  referenceCapital: number,
  morteCapital: number,
  assistanceUmbrella: CoverageUmbrella | null,
  capitalizationUmbrella: CoverageUmbrella | null,
  riskProfile?: RiskEntityPayload
): Partial<StepCoveragesFormValues> {
  const coverages: StepCoveragesFormValues['coverages'] = {};
  const assistances: StepCoveragesFormValues['assistances'] = {};
  let capitalization = '';
  let assistanceFuneral = '';

  const apiCoverages = resolveCoveragesForPrefill(entry, riskProfile);
  const coverageCapitalsByCode = new Map<string, number>();

  for (const apiCoverage of apiCoverages) {
    const catalogItem = coverageCatalog.find(
      c => c.productElementId === Number(apiCoverage.productElementId)
    );
    const capital = apiCoverage.value ?? 0;
    if (catalogItem && capital > 0) {
      coverageCapitalsByCode.set(catalogItem.code, capital);
    }
  }

  for (const apiCoverage of apiCoverages) {
    const productElementId = Number(apiCoverage.productElementId);
    const catalogItem = coverageCatalog.find(
      c => c.productElementId === productElementId
    );

    if (catalogItem) {
      const limitsCtx = {
        referenceCapital,
        morteCapital,
        morteId,
        coverageCapitalsByCode,
      };
      const limits = getEffectiveLimits(catalogItem, limitsCtx);
      const capital = apiCoverage.value ?? 0;
      const percentRef = getPercentReferenceCapital(catalogItem, limitsCtx);
      const { capital: cappedCapital, percent } = capCapitalAndPercent({
        capital,
        percentRefCapital: percentRef,
        effectiveMax: limits.max > 0 ? limits.max : capital,
        effectiveMin: limits.min,
      });
      // Cobertura contratada por benefício: o attach guardou a opção em
      // `benefits` — é ela que reidrata o seletor (o capital pode vir 0,
      // quando a opção não tem valor em R$). Cotação salva ANTES do envio de
      // benefits só tem o valor: casa pelo capital para não abrir a tela com o
      // seletor vazio.
      const benefitCode =
        (apiCoverage.benefits ?? [])[0]?.code ??
        (cappedCapital > 0
          ? (catalogItem.benefitOptions?.find(
              option => option.capital === cappedCapital
            )?.code ?? '')
          : '');

      coverages[catalogItem.id] = {
        enabled: true,
        maxIndividualCapital:
          cappedCapital > 0 ? formatCurrency(cappedCapital) : '',
        percent:
          percent ||
          (morteCapital > 0 && cappedCapital > 0
            ? roundCoveragePercent((cappedCapital / morteCapital) * 100)
            : ''),
        benefitCode,
      };
      continue;
    }

    if (
      assistanceUmbrella &&
      String(assistanceUmbrella.id) === String(productElementId)
    ) {
      for (const benefit of apiCoverage.benefits ?? []) {
        const benefitItem = assistanceUmbrella.benefits.find(
          b => b.code === benefit.code
        );
        if (!benefitItem) continue;
        if (isGroupedAssistanceBenefit(benefitItem)) {
          assistanceFuneral = benefit.code;
        } else if (isStandaloneAssistanceBenefit(benefitItem)) {
          assistances[benefit.code] = { enabled: true };
        }
      }
      continue;
    }

    if (
      capitalizationUmbrella &&
      String(capitalizationUmbrella.id) === String(productElementId)
    ) {
      const benefit = (apiCoverage.benefits ?? [])[0];
      if (benefit?.code) {
        capitalization = benefit.code;
      }
    }
  }

  return { coverages, assistances, capitalization, assistanceFuneral };
}

// ─── get-reference-capital-for-entity ───────────────────────────────────────

export function getReferenceCapitalForEntity(
  proposalDetails: ProposalDetails | undefined,
  entityDocumentNumber: string
): number {
  const profiles = proposalDetails?.riskProfileList;
  if (!profiles || profiles.length === 0) return 0;

  const cleanTarget = removeFormatRegisterNumber(entityDocumentNumber);
  const match = profiles.find(
    p => removeFormatRegisterNumber(p.documentNumber) === cleanTarget
  );

  return getReferenceCapital(match);
}

// ─── step-3 resync helpers ───────────────────────────────────────────────────

export function getApiMorteValueForCoverageEntry(
  entry: CoverageProfileEntry,
  morteId: string | undefined,
  coverageCatalog: CapitalGlobalCoverageCatalogItem[],
  riskProfile?: RiskEntityPayload
): number {
  if (!morteId) return 0;

  const apiCoverages = resolveCoveragesForPrefill(entry, riskProfile);

  const morteApiCoverage = apiCoverages.find(c => {
    const catalogItem = coverageCatalog.find(ci => ci.id === morteId);
    return (
      catalogItem && Number(c.productElementId) === catalogItem.productElementId
    );
  });

  return morteApiCoverage?.value && morteApiCoverage.value > 0
    ? morteApiCoverage.value
    : 0;
}

export function reconcileEntityCoveragesFromStep2(
  coverages: CoveragesEntityValues['coverages'],
  morteId: string,
  referenceCapital: number,
  coverageCatalog: CapitalGlobalCoverageCatalogItem[]
): CoveragesEntityValues['coverages'] {
  const morteItem = coverageCatalog.find(c => c.id === morteId);
  if (!morteItem || referenceCapital <= 0) return coverages;

  let updated = {
    ...coverages,
    [morteId]: {
      enabled: true,
      ...getInitialCoverageValues(
        morteItem,
        {
          referenceCapital,
          morteCapital: referenceCapital,
          morteId,
        },
        formatCurrency
      ),
    },
  };

  updated = propagateMorteCapitalChange(
    updated,
    morteId,
    referenceCapital,
    coverageCatalog,
    referenceCapital
  );

  return updated;
}

export type MapCoveragesDataMapOptions = {
  resyncFromStep2?: boolean;
};

// ─── map-coverages-data-map-from-api ─────────────────────────────────────────

const DEFAULT_COVERAGES_ENTITY_VALUES: CoveragesEntityValues = {
  coverages: {},
  assistances: {},
  capitalization: '',
  assistanceFuneral: '',
};

export function mapCoveragesDataMapFromApi(
  proposalDetails: ProposalDetails | undefined,
  coverageCatalog: CapitalGlobalCoverageCatalogItem[],
  morteId: string | undefined,
  assistanceUmbrella: CoverageUmbrella | null,
  capitalizationUmbrella: CoverageUmbrella | null,
  options?: MapCoveragesDataMapOptions
): CoveragesDataMap {
  const entries = proposalDetails?.coverageProfileList;
  if (!entries || entries.length === 0) return {};

  const profiles = proposalDetails?.riskProfileList ?? [];
  const result: CoveragesDataMap = {};

  for (const entry of entries) {
    const cleanDoc = removeFormatRegisterNumber(entry.documentNumber);
    if (!cleanDoc) continue;

    const riskProfile = profiles.find(
      p => removeFormatRegisterNumber(p.documentNumber) === cleanDoc
    );
    const referenceCapital = getReferenceCapital(riskProfile);

    const apiMorteValue = getApiMorteValueForCoverageEntry(
      entry,
      morteId,
      coverageCatalog,
      riskProfile
    );

    // Keep API morte for percent derivation; reconcile when step-2 reference diverges.
    const morteValue = apiMorteValue > 0 ? apiMorteValue : referenceCapital;

    const prefilled = mapCoverageProfileToFormValues(
      entry,
      coverageCatalog,
      morteId,
      referenceCapital,
      morteValue,
      assistanceUmbrella,
      capitalizationUmbrella,
      riskProfile
    );

    let coverages = prefilled.coverages ?? {};

    const shouldReconcileFromReferenceCapital =
      options?.resyncFromStep2 === true &&
      Boolean(morteId) &&
      referenceCapital > 0;

    if (shouldReconcileFromReferenceCapital && morteId) {
      coverages = reconcileEntityCoveragesFromStep2(
        coverages,
        morteId,
        referenceCapital,
        coverageCatalog
      );
    }

    result[cleanDoc] = {
      coverages,
      assistances: prefilled.assistances ?? {},
      capitalization: prefilled.capitalization ?? '',
      assistanceFuneral: prefilled.assistanceFuneral ?? '',
    };
  }

  return result;
}

export { DEFAULT_COVERAGES_ENTITY_VALUES };
